import { useEffect, useId, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { testId } from '../../app/testId';
import { focusIntoWhenPaintable, restoreFocus } from '../a11y/focus';
import { useEscapeLayer, useOutsideClick } from '../a11y/layers';
import { nextRovingIndex } from '../a11y/roving';
import { usePosition, type Placement } from '../a11y/usePosition';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon';
import styles from './Menu.module.css';

// Menu — DESIGN_SYSTEM §12 row 32 (`.menu`): row actions, the account switcher, the mode menu. WAI-ARIA
// menu-button pattern: `role="menu"` > `role="menuitem"` buttons, roving focus (↑ ↓ wrap, Home / End,
// first-letter typeahead), Enter / Space select, Escape or a click outside closes, Tab closes; on close, focus
// returns to the anchor (TEST_PLAN §1.6 K6). Controlled like Popover: the trigger carries `aria-haspopup="menu"`
// + `aria-expanded` + `aria-controls={menuId}`. Items are data so the row menu and the mode menu are one
// component; a danger item is red, a separator is a hairline, a label is an uppercase group heading.

export interface MenuItem {
  type?: 'item';
  key: string;
  label: string;
  icon?: IconName;
  /** A hint at the end ("B", "↵") — decoration; the screen binds the key itself. */
  shortcut?: string;
  /** Destructive ("Remove…"): red text and icon. */
  danger?: boolean;
  disabled?: boolean;
  /** A second line or a trailing note ("Current"). */
  end?: string;
  onSelect: () => void;
  testID?: string;
}
export interface MenuSeparator {
  type: 'separator';
  key: string;
}
export interface MenuLabel {
  type: 'label';
  key: string;
  label: string;
}
export type MenuEntry = MenuItem | MenuSeparator | MenuLabel;

export interface MenuProps {
  open: boolean;
  onClose: () => void;
  /** The trigger. Focus returns here on close. */
  anchorRef: RefObject<HTMLElement | null>;
  /**
   * Focus returns here on close instead of the anchor — T-076-W8. `Modal` and `Popover` both had this escape
   * hatch and the menu did not, so a menu whose own trigger is removed by the action it ran (a row menu on a
   * row that goes away) had nowhere to send the keyboard.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
  items: readonly MenuEntry[];
  /** The menu's accessible name (one of the two is required). */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  placement?: Placement;
  width?: number | string;
  /** Root test id; each item gets `<testID>.<key>` unless it names its own. */
  testID?: string;
  /** Element id, for the trigger's `aria-controls`. */
  id?: string;
  className?: string;
}

function isItem(entry: MenuEntry): entry is MenuItem {
  return entry.type === undefined || entry.type === 'item';
}

export function Menu({
  open,
  onClose,
  anchorRef,
  returnFocusRef,
  items,
  placement = 'bottom-end',
  width,
  testID,
  id,
  className,
  ...labelling
}: MenuProps) {
  if (import.meta.env.DEV && !labelling['aria-label'] && !labelling['aria-labelledby']) {
    throw new Error('Menu needs an aria-label or aria-labelledby (REQ-281).');
  }
  const generated = useId();
  const menuId = id ?? `menu-${generated}`;
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const position = usePosition({ open, anchorRef, floatingRef: menuRef, placement });
  const enabled = items
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => isItem(entry) && !entry.disabled)
    .map(({ index }) => index);
  const [active, setActive] = useState<number>(enabled[0] ?? -1);

  useEscapeLayer(open, onClose);
  useOutsideClick(open, [menuRef, anchorRef], onClose);

  // Focus the first item on open — but only once `usePosition` has placed the menu. For one commit it is
  // `visibility: hidden` while the anchor is measured, and a real browser refuses to focus anything inside a
  // `visibility: hidden` subtree (BUG-020, found on the date panel; the same effect shape was here).
  const positioned = position.style.visibility !== 'hidden';
  const tookFocus = useRef(false);
  useEffect(() => {
    if (!open) {
      tookFocus.current = false;
      return;
    }
    if (!positioned || tookFocus.current) return;
    const menu = menuRef.current;
    if (!menu) return;
    tookFocus.current = true;
    const first = enabled[0] ?? -1;
    setActive(first);
    return focusIntoWhenPaintable(menu, itemRefs.current[first]);
    // `enabled` is derived from `items`; re-focusing on every items change would steal focus.
  }, [open, positioned]);

  // Focus back to the trigger (or wherever the caller says) on close or unmount — its own effect, so the
  // one above can re-run without handing the focus back in between.
  useEffect(() => {
    if (!open) return;
    return () => restoreFocus(returnFocusRef?.current ?? anchorRef.current);
  }, [open, anchorRef, returnFocusRef]);

  if (!open) return null;

  const focusIndex = (index: number) => {
    setActive(index);
    itemRefs.current[index]?.focus({ preventScroll: true });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (enabled.length === 0) return;
    if (event.key === 'Tab') {
      event.preventDefault();
      onClose();
      return;
    }
    const current = Math.max(enabled.indexOf(active), 0);
    const next = nextRovingIndex(event.key, current, enabled.length);
    if (next !== null) {
      event.preventDefault();
      focusIndex(enabled[next] as number);
      return;
    }
    // Typeahead: the next item whose label starts with the typed letter (wrapping past the current one).
    if (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
      const letter = event.key.toLowerCase();
      for (let step = 1; step <= enabled.length; step += 1) {
        const index = enabled[(current + step) % enabled.length] as number;
        const entry = items[index] as MenuItem;
        if (entry.label.toLowerCase().startsWith(letter)) {
          event.preventDefault();
          focusIndex(index);
          return;
        }
      }
    }
  };

  return createPortal(
    <div
      ref={menuRef}
      id={menuId}
      role="menu"
      className={cx(styles.menu, className)}
      style={{ ...position.style, width }}
      data-side={position.side}
      data-testid={testID}
      onKeyDown={onKeyDown}
      {...labelling}
    >
      {items.map((entry, index) => {
        if (entry.type === 'separator') {
          return <div key={entry.key} role="separator" className={styles.separator} />;
        }
        if (entry.type === 'label') {
          return (
            <div key={entry.key} role="presentation" className={styles.label}>
              {entry.label}
            </div>
          );
        }
        const itemTestId = entry.testID ?? (testID ? testId(testID, entry.key) : undefined);
        return (
          <button
            key={entry.key}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            type="button"
            role="menuitem"
            tabIndex={index === active ? 0 : -1}
            aria-disabled={entry.disabled || undefined}
            className={cx(styles.item, entry.danger && styles.danger)}
            data-testid={itemTestId}
            data-danger={entry.danger || undefined}
            onPointerMove={() => {
              if (!entry.disabled && active !== index) focusIndex(index);
            }}
            onClick={() => {
              if (entry.disabled) return;
              entry.onSelect();
              onClose();
            }}
          >
            {entry.icon ? <Icon name={entry.icon} /> : null}
            <span className={styles.itemLabel}>{entry.label}</span>
            {entry.end ? <span className={styles.end}>{entry.end}</span> : null}
            {entry.shortcut ? (
              <kbd className={styles.kbd} aria-hidden="true">
                {entry.shortcut}
              </kbd>
            ) : null}
          </button>
        );
      })}
    </div>,
    document.body,
  );
}
